import { useState, useEffect } from 'react';
import '@/App.css';
import axios from 'axios';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import CityPage from './pages/CityPage';
import { cities, cityPath, serviceAreaDirectory } from './cityData';
import { trackEvent } from './track';
import { Phone, Mail, Clock, Shield, Award, CheckCircle, Wrench, Star, MapPin, MessageCircle, X, ChevronDown, ChevronUp, Zap, Gift, CreditCard, Banknote, Smartphone, FileCheck, ClipboardCheck, ThumbsUp, Menu } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Home({ scrollToContact = false }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    appliance_type: '',
    issue_description: '',
    preferred_datetime: '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showStickyCall, setShowStickyCall] = useState(false);
  const [showWhatsApp, setShowWhatsApp] = useState(true);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Show sticky call button after scrolling
  useEffect(() => {
    const handleScroll = () => {
      setShowStickyCall(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (scrollToContact) {
      const t = setTimeout(() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }), 400);
      return () => clearTimeout(t);
    }
  }, [scrollToContact]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.appliance_type) {
      setSubmitMessage('Please select an appliance type below.');
      return;
    }
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await axios.post(`${API}/service-request`, formData);
      if (response.status === 200) {
        setSubmitMessage('Thank you! We will contact you shortly.');
        setFormData({
          name: '',
          phone: '',
          email: '',
          appliance_type: '',
          issue_description: '',
          preferred_datetime: '',
          address: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage('Sorry, there was an error. Please call us directly at 786-696-4010.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // SVG Icon components for appliances
  const RefrigeratorIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="25" y="10" width="50" height="80" fill="none" stroke="#2563eb" strokeWidth="3" rx="4"/>
      <line x1="25" y1="40" x2="75" y2="40" stroke="#2563eb" strokeWidth="3"/>
      <line x1="60" y1="20" x2="60" y2="35" stroke="#2563eb" strokeWidth="2"/>
      <line x1="60" y1="50" x2="60" y2="75" stroke="#2563eb" strokeWidth="2"/>
    </svg>
  );

  const DishwasherIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="20" y="15" width="60" height="70" fill="none" stroke="#2563eb" strokeWidth="3" rx="4"/>
      <rect x="25" y="20" width="50" height="10" fill="#2563eb" opacity="0.2"/>
      <circle cx="35" cy="25" r="2" fill="#2563eb"/>
      <circle cx="45" cy="25" r="2" fill="#2563eb"/>
      <circle cx="55" cy="25" r="2" fill="#2563eb"/>
      <circle cx="50" cy="55" r="15" fill="none" stroke="#2563eb" strokeWidth="2"/>
      <circle cx="50" cy="55" r="8" fill="none" stroke="#2563eb" strokeWidth="2"/>
    </svg>
  );

  const WasherIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="20" y="15" width="60" height="70" fill="none" stroke="#2563eb" strokeWidth="3" rx="4"/>
      <rect x="25" y="20" width="50" height="10" fill="#2563eb" opacity="0.2"/>
      <circle cx="35" cy="25" r="2" fill="#2563eb"/>
      <circle cx="45" cy="25" r="2" fill="#2563eb"/>
      <circle cx="50" cy="57" r="20" fill="none" stroke="#2563eb" strokeWidth="3"/>
      <circle cx="50" cy="57" r="13" fill="none" stroke="#2563eb" strokeWidth="2" strokeDasharray="5,3"/>
    </svg>
  );

  const DryerIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="20" y="15" width="60" height="70" fill="none" stroke="#2563eb" strokeWidth="3" rx="4"/>
      <rect x="25" y="20" width="50" height="10" fill="#2563eb" opacity="0.2"/>
      <circle cx="60" cy="25" r="3" fill="#2563eb"/>
      <circle cx="50" cy="57" r="20" fill="none" stroke="#2563eb" strokeWidth="3"/>
      <path d="M 40 57 Q 50 47, 60 57 T 40 57" fill="none" stroke="#2563eb" strokeWidth="2"/>
    </svg>
  );

  const MicrowaveIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="15" y="30" width="70" height="45" fill="none" stroke="#2563eb" strokeWidth="3" rx="4"/>
      <rect x="20" y="35" width="45" height="35" fill="none" stroke="#2563eb" strokeWidth="2"/>
      <circle cx="75" cy="45" r="3" fill="#2563eb"/>
      <circle cx="75" cy="55" r="3" fill="#2563eb"/>
      <circle cx="75" cy="65" r="3" fill="#2563eb"/>
      <line x1="30" y1="52" x2="55" y2="52" stroke="#2563eb" strokeWidth="1" opacity="0.3"/>
    </svg>
  );

  const CooktopIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="15" y="35" width="70" height="35" fill="none" stroke="#2563eb" strokeWidth="3" rx="4"/>
      <circle cx="35" cy="52" r="8" fill="none" stroke="#2563eb" strokeWidth="2"/>
      <circle cx="35" cy="52" r="4" fill="none" stroke="#2563eb" strokeWidth="2"/>
      <circle cx="65" cy="52" r="8" fill="none" stroke="#2563eb" strokeWidth="2"/>
      <circle cx="65" cy="52" r="4" fill="none" stroke="#2563eb" strokeWidth="2"/>
    </svg>
  );

  const OvenIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="20" y="15" width="60" height="70" fill="none" stroke="#2563eb" strokeWidth="3" rx="4"/>
      <rect x="25" y="20" width="50" height="12" fill="#2563eb" opacity="0.2"/>
      <circle cx="35" cy="26" r="2" fill="#2563eb"/>
      <circle cx="45" cy="26" r="2" fill="#2563eb"/>
      <circle cx="55" cy="26" r="2" fill="#2563eb"/>
      <circle cx="65" cy="26" r="2" fill="#2563eb"/>
      <rect x="28" y="40" width="44" height="38" fill="none" stroke="#2563eb" strokeWidth="2"/>
      <line x1="35" y1="45" x2="65" y2="45" stroke="#2563eb" strokeWidth="1"/>
      <line x1="35" y1="73" x2="65" y2="73" stroke="#2563eb" strokeWidth="1"/>
    </svg>
  );

  const AllBrandsIcon = ({ className = "w-20 h-20 mx-auto" }) => (
    <svg viewBox="0 0 100 100" className={className}>
      <rect x="25" y="20" width="20" height="25" fill="none" stroke="#2563eb" strokeWidth="2" rx="2"/>
      <circle cx="35" cy="32" r="5" fill="none" stroke="#2563eb" strokeWidth="2"/>
      <rect x="55" y="20" width="20" height="25" fill="none" stroke="#2563eb" strokeWidth="2" rx="2"/>
      <rect x="58" y="23" width="14" height="10" fill="none" stroke="#2563eb" strokeWidth="1.5"/>
      <rect x="25" y="55" width="20" height="25" fill="none" stroke="#2563eb" strokeWidth="2" rx="2"/>
      <line x1="28" y1="62" x2="42" y2="62" stroke="#2563eb" strokeWidth="1.5"/>
      <rect x="55" y="55" width="20" height="25" fill="none" stroke="#2563eb" strokeWidth="2" rx="2"/>
      <circle cx="65" cy="67" r="5" fill="none" stroke="#2563eb" strokeWidth="2"/>
    </svg>
  );

  const services = [
    { name: 'Refrigerators', value: 'refrigerator', IconComponent: RefrigeratorIcon },
    { name: 'Dishwashers', value: 'dishwasher', IconComponent: DishwasherIcon },
    { name: 'Washers', value: 'washer', IconComponent: WasherIcon },
    { name: 'Dryers', value: 'dryer', IconComponent: DryerIcon },
    { name: 'Microwaves', value: 'microwave', IconComponent: MicrowaveIcon },
    { name: 'Cooktops', value: 'cooktop', IconComponent: CooktopIcon },
    { name: 'Ovens', value: 'oven', IconComponent: OvenIcon },
    { name: 'All Brands', value: null, IconComponent: AllBrandsIcon }
  ];

  const handleServiceClick = (value) => {
    if (value) {
      setFormData(prev => ({ ...prev, appliance_type: value }));
    }
    setSubmitMessage('');
    trackEvent('begin_booking', { source: 'service-card' });
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const applianceOptions = [
    { value: 'refrigerator', label: 'Refrigerator', IconComponent: RefrigeratorIcon },
    { value: 'dishwasher', label: 'Dishwasher', IconComponent: DishwasherIcon },
    { value: 'washer', label: 'Washer', IconComponent: WasherIcon },
    { value: 'dryer', label: 'Dryer', IconComponent: DryerIcon },
    { value: 'microwave', label: 'Microwave', IconComponent: MicrowaveIcon },
    { value: 'cooktop', label: 'Cooktop', IconComponent: CooktopIcon },
    { value: 'oven', label: 'Oven', IconComponent: OvenIcon },
    { value: 'other', label: 'Other', IconComponent: null }
  ];

  const testimonials = [
    {
      name: 'Maria G.',
      location: 'Boca Raton',
      appliance: 'Sub-Zero Refrigerator',
      text: 'My Sub-Zero stopped cooling and he came out the same day. Diagnosed it fast, had the part on hand, and fixed it in about an hour. Professional and honest pricing.'
    },
    {
      name: 'James R.',
      location: 'Fort Lauderdale',
      appliance: 'Whirlpool Washer',
      text: 'Second time using this company. Showed up on time, explained exactly what was wrong, and the quote was fair. Washer runs like new again.'
    },
    {
      name: 'Olga P.',
      location: 'Miami',
      appliance: 'Miele Dishwasher',
      text: 'Very knowledgeable with high-end brands. Fixed our Miele dishwasher that two other companies could not figure out. Highly recommend!'
    }
  ];

  const whyChooseUs = [
    { title: '7 Years Experience', description: 'Trusted expertise in appliance repair', icon: <Award className="w-8 h-8 text-blue-600" /> },
    { title: 'Licensed & Insured', description: 'Fully certified and protected', icon: <Shield className="w-8 h-8 text-blue-600" /> },
    { title: 'Same-Day Service', description: 'Fast response when you need it', icon: <Clock className="w-8 h-8 text-blue-600" /> },
    { title: 'Upfront Pricing', description: 'Honest, transparent quotes', icon: <CheckCircle className="w-8 h-8 text-blue-600" /> },
    { title: 'Warranty Included', description: 'Labor & parts guaranteed', icon: <Wrench className="w-8 h-8 text-blue-600" /> },
    { title: 'Repeat Customers', description: 'Large base of satisfied clients', icon: <Star className="w-8 h-8 text-blue-600" /> }
  ];

  const faqs = [
    {
      question: "How long does a typical repair take?",
      answer: "Most repairs are completed within 1-2 hours on the same visit. Complex repairs requiring special parts may need a follow-up visit, but we'll always give you an accurate timeline upfront."
    },
    {
      question: "Do you really offer same-day service?",
      answer: "Yes! We offer same-day service 7 days a week — 7:30 AM to 9 PM Sunday through Friday, and 8 AM to 5 PM on Saturdays. Call us early in the day for the best availability, especially for urgent repairs."
    },
    {
      question: "What if my appliance can't be fixed?",
      answer: "We'll provide an honest assessment. If repair isn't cost-effective, we'll explain why and help you understand your options. You only pay the $80 service call fee - no repair charges if we don't fix it."
    },
    {
      question: "Do you warranty your repairs?",
      answer: "Absolutely! We provide a warranty on both labor and parts. We stand behind our work and want you to have peace of mind."
    },
    {
      question: "What brands do you service?",
      answer: "We service ALL major brands including Samsung, LG, Whirlpool, GE, Maytag, Frigidaire, Bosch, and KitchenAid. We also specialize in high-end brands like Miele, Sub-Zero, Wolf, Thermador, and Viking."
    },
    {
      question: "How much will it cost?",
      answer: "We charge an $80 service call fee which is included in the total repair price if you proceed. We provide upfront quotes before starting any work - no hidden fees, ever."
    }
  ];

  return (
    <div id="top" className="App">
      {/* Urgency Banner */}
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-2.5 px-4 text-center font-semibold text-sm md:text-base shadow-lg">
        <span className="inline-flex items-center flex-wrap justify-center gap-x-2 gap-y-0.5">
          <Zap className="w-4 h-4 fill-yellow-300 text-yellow-300" />
          <span className="animate-pulse">LIMITED SLOTS TODAY!</span> <span className="hidden sm:inline">Same-Day Service Available - </span>Call Now: <a href="tel:7866964010" onClick={() => trackEvent('click_to_call')} className="underline hover:text-yellow-200 whitespace-nowrap">786-696-4010</a>
        </span>
      </div>

      {/* Sticky Header */}
      <header data-testid="site-header" className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <a href="#top" className="flex items-center gap-2" data-testid="header-logo">
            <img src="/logo.png" alt="Appliances Repair MCulai logo" className="w-10 h-10 md:w-11 md:h-11 object-contain" />
            <div className="leading-tight text-left">
              <span className="block text-sm md:text-lg font-bold text-gray-900">Appliances Repair MCulai</span>
              <span className="hidden sm:block text-xs text-gray-500">Same-Day Service • South Florida</span>
            </div>
          </a>
          <nav className="hidden lg:flex items-center gap-6 text-sm font-semibold text-gray-700">
            <a href="#services" className="hover:text-blue-600 transition-colors" data-testid="nav-services">Services</a>
            <a href="#how-it-works" className="hover:text-blue-600 transition-colors" data-testid="nav-how-it-works">How It Works</a>
            <a href="#pricing" className="hover:text-blue-600 transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#reviews" className="hover:text-blue-600 transition-colors" data-testid="nav-reviews">Reviews</a>
            <a href="#faq" className="hover:text-blue-600 transition-colors" data-testid="nav-faq">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <a href="#contact" onClick={() => trackEvent('begin_booking')} className="hidden md:inline-flex bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors" data-testid="header-book-button">Book Online</a>
            <a href="tel:7866964010" onClick={() => trackEvent('click_to_call')} className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-colors" data-testid="header-call-button">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">786-696-4010</span>
              <span className="sm:hidden">Call</span>
            </a>
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden p-2 text-gray-700 hover:text-blue-600"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        {showMobileMenu && (
          <nav className="lg:hidden bg-white border-t border-gray-100 px-4 py-3 flex flex-col gap-1 shadow-lg" data-testid="mobile-menu">
            {[['#services', 'Services'], ['#how-it-works', 'How It Works'], ['#pricing', 'Pricing'], ['#reviews', 'Reviews'], ['#faq', 'FAQ']].map(([href, label]) => (
              <a key={href} href={href} onClick={() => setShowMobileMenu(false)} className="py-2.5 px-2 font-semibold text-gray-700 hover:text-blue-600 rounded-lg hover:bg-gray-50">{label}</a>
            ))}
            <a href="#contact" onClick={() => { setShowMobileMenu(false); trackEvent('begin_booking'); }} className="mt-1 bg-blue-600 hover:bg-blue-700 text-white text-center py-2.5 rounded-lg font-bold" data-testid="mobile-menu-book">Book Online</a>
          </nav>
        )}
      </header>

      {/* Sticky Call Button */}
      {showStickyCall && (
        <a
          href="tel:7866964010"
          onClick={() => trackEvent('click_to_call')}
          className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white px-6 py-4 rounded-full shadow-2xl flex items-center gap-2 font-bold text-lg transition-all transform hover:scale-110 animate-bounce"
          data-testid="sticky-call-button"
        >
          <Phone className="w-6 h-6" />
          <span className="hidden md:inline">Call Now</span>
        </a>
      )}

      {/* WhatsApp/SMS Floating Button */}
      {showWhatsApp && (
        <div className="fixed bottom-6 left-6 z-50">
          <button
            onClick={() => setShowWhatsApp(false)}
            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <X className="w-4 h-4" />
          </button>
          <a
            href="sms:+17866964010?body=Hi%20MCulai%2C%20I%20need%20appliance%20repair%20for%20my%20"
            onClick={() => trackEvent('click_to_sms')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-5 py-4 rounded-full shadow-2xl flex items-center gap-2 font-semibold transition-all transform hover:scale-110"
            data-testid="sms-button"
          >
            <MessageCircle className="w-6 h-6" />
            <span>Text Us</span>
          </a>
        </div>
      )}
      {/* Hero Section */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 text-white">
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c?auto=compress&cs=tinysrgb&w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.3
        }}></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
          <div className="mb-6 flex flex-wrap justify-center gap-3">
            <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">Same-Day Service</span>
            <span className="bg-blue-500 text-white px-4 py-2 rounded-full text-sm font-semibold">Licensed & Insured</span>
            <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-semibold">7 Years Experience</span>
          </div>
          
          <h1 data-testid="hero-title" className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Fast, Reliable Appliance Repair
            <span className="block text-3xl md:text-5xl mt-4 text-blue-200">Across South Florida</span>
          </h1>
          
          <p className="voice-answer-summary text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
            Appliances Repair MCulai provides same-day appliance repair across South Florida, from Miami to Boynton Beach. We fix refrigerators, dishwashers, washers, dryers, microwaves, cooktops, ovens, wine coolers and more — 7 days a week.
          </p>
          
          <div className="mb-8">
            <span data-testid="hero-offer-badge" className="inline-flex items-center gap-2 bg-yellow-400 text-blue-950 px-6 py-3 rounded-full font-bold text-base md:text-lg shadow-lg">
              <Gift className="w-5 h-5" />
              New Customers: $25 OFF Your Repair
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <a 
              href="tel:7866964010" 
              data-testid="hero-call-button"
              onClick={() => trackEvent('click_to_call')}
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg text-xl font-bold flex items-center gap-2 transition-all transform hover:scale-105 shadow-lg"
            >
              <Phone className="w-6 h-6" />
              Call Now: 786-696-4010
            </a>
            <a 
              href="#contact" 
              data-testid="hero-quote-button"
              onClick={() => trackEvent('begin_booking')}
              className="bg-white text-blue-900 hover:bg-gray-100 px-8 py-4 rounded-lg text-xl font-bold transition-all transform hover:scale-105 shadow-lg"
            >
              Schedule Service
            </a>
          </div>
          
          <p className="emergency-dispatch-info text-lg text-gray-300">
            <Clock className="inline w-5 h-5 mr-2" />
            Open 7 Days: Sun–Fri 7:30 AM – 9 PM • Sat 8 AM – 5 PM
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" data-testid="services-section" className="py-20 bg-gray-50 scroll-mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">All Brands • All Makes & Models</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = service.IconComponent;
              return (
                <button 
                  key={index} 
                  type="button"
                  data-testid={`service-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => handleServiceClick(service.value)}
                  className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-xl transition-all hover:scale-105 transform duration-200 cursor-pointer group"
                >
                  <div className="mb-3">
                    <IconComponent />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                  <span className="block mt-2 text-sm font-semibold text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity">Book Service →</span>
                </button>
              );
            })}
          </div>
          
          <div className="mt-16 text-center">
            <p className="text-2xl font-bold text-blue-900 mb-6">We Service All Major Brands</p>
            <div data-testid="brand-strip" className="flex flex-wrap justify-center gap-3 mb-10">
              {['Whirlpool', 'GE (General Electric)', 'GE Profile', 'Samsung', 'LG', 'Frigidaire', 'Maytag', 'KitchenAid', 'Bosch', 'Kenmore', 'Amana', 'Hotpoint', 'Electrolux', 'InSinkErator'].map((brand) => (
                <span key={brand} className="bg-white border border-gray-200 text-gray-700 px-5 py-2.5 rounded-lg font-bold text-base md:text-lg shadow-sm hover:shadow-md hover:border-blue-300 transition-all">
                  {brand}
                </span>
              ))}
            </div>
            <p className="text-xl font-semibold text-blue-800 mb-4">Specializing in High-End Brands</p>
            <div data-testid="premium-brand-strip" className="flex flex-wrap justify-center gap-3">
              {['Sub-Zero', 'Wolf', 'Cove', 'Miele', 'Thermador', 'Viking', 'Gaggenau', 'Speed Queen', 'Café', 'Monogram', 'JennAir', 'Dacor', 'Fisher & Paykel', 'U-Line'].map((brand) => (
                <span key={brand} className="bg-blue-950 text-white px-5 py-2.5 rounded-lg font-bold text-base md:text-lg tracking-wide shadow-md hover:bg-blue-900 transition-colors">
                  {brand}
                </span>
              ))}
            </div>
            <p data-testid="extra-services-note" className="mt-10 text-base md:text-lg text-gray-600">
              Also servicing: wine coolers • freezers • ice makers • commercial kitchen & laundry appliances • yacht & marine appliances
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" data-testid="how-it-works-section" className="py-20 bg-white scroll-mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600">Getting your appliance fixed is easy — 3 simple steps</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div data-testid="how-step-1" className="relative bg-gray-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-md">1</div>
              <Phone className="w-12 h-12 mx-auto mb-4 mt-4 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Call or Book Online</h3>
              <p className="text-gray-600">Tell us what's broken. We'll schedule your visit — same-day slots available 7 days a week.</p>
            </div>
            
            <div data-testid="how-step-2" className="relative bg-gray-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-md">2</div>
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 mt-4 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Diagnosis</h3>
              <p className="text-gray-600">$80 service call — included in your repair price. You get an upfront quote before any work begins.</p>
            </div>
            
            <div data-testid="how-step-3" className="relative bg-gray-50 rounded-2xl p-8 text-center hover:shadow-lg transition-shadow">
              <div className="absolute -top-5 left-1/2 -translate-x-1/2 bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold shadow-md">3</div>
              <ThumbsUp className="w-12 h-12 mx-auto mb-4 mt-4 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fixed & Guaranteed</h3>
              <p className="text-gray-600">Most repairs done in 1-2 hours on the same visit. Parts & labor warranty included.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section data-testid="why-choose-section" className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
            <p className="text-xl text-gray-600">Your trusted appliance repair experts in South Florida</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUs.map((item, index) => (
              <div key={index} data-testid={`feature-${index}`} className="flex flex-col items-center text-center p-6 rounded-lg hover:bg-gray-50 transition-colors">
                <div className="mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section data-testid="service-area-section" className="py-20 bg-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MapPin className="w-16 h-16 mx-auto mb-6 text-blue-300" />
          <h2 className="text-4xl md:text-5xl font-bold mb-6">We Serve All South Florida</h2>
          <p className="text-2xl mb-8">From Miami to Boynton Beach</p>
          <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            <div className="bg-blue-800 rounded-lg p-4">
              <p className="text-lg">Miami • Miami Beach • Coral Gables</p>
            </div>
            <div className="bg-blue-800 rounded-lg p-4">
              <p className="text-lg">Fort Lauderdale • Hollywood • Pompano Beach</p>
            </div>
            <div className="bg-blue-800 rounded-lg p-4">
              <p className="text-lg">Boca Raton • Delray Beach • Boynton Beach</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" data-testid="pricing-section" className="py-20 bg-gray-50 scroll-mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Special Offer Banner */}
          <div className="mb-12 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl p-8 text-center shadow-xl transform hover:scale-105 transition-transform">
            <Gift className="w-14 h-14 mx-auto mb-3" />
            <h3 className="text-3xl font-bold mb-3">Special Offer for New Customers!</h3>
            <p className="text-2xl font-semibold mb-2">$25 OFF Your Repair</p>
            <p className="text-lg opacity-90">Mention this website when you call • Limited time offer</p>
          </div>

          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Transparent Pricing</h2>
            <p className="text-xl text-gray-600">No hidden fees, no surprises</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="text-6xl font-bold text-blue-900 mb-4">$80</div>
              <p className="text-2xl text-gray-700 font-semibold mb-2">Service Call Fee</p>
              <p className="text-lg text-green-600 font-semibold">✓ Included in repair price if you proceed</p>
            </div>
            
            <div className="space-y-4 max-w-2xl mx-auto">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><strong>Upfront quotes</strong> - Know the cost before we start</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><strong>No hidden fees</strong> - Transparent pricing always</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                <p className="text-gray-700"><strong>Warranty included</strong> - Parts and labor guaranteed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" data-testid="reviews-section" className="py-20 bg-white scroll-mt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">What Our Customers Say</h2>
          <div className="flex justify-center gap-2 mb-3">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star key={star} className="w-8 h-8 text-yellow-400 fill-yellow-400" />
            ))}
          </div>
          <p className="text-lg text-gray-500 mb-12">Hundreds of satisfied customers across South Florida</p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-12 text-left">
            {testimonials.map((review, index) => (
              <div key={index} data-testid={`testimonial-${index}`} className="bg-gray-50 rounded-2xl p-6 shadow-md hover:shadow-lg transition-shadow flex flex-col">
                <div className="flex gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-4 flex-grow">"{review.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{review.name}</p>
                  <p className="text-sm text-gray-500">{review.location} • {review.appliance}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://maps.app.goo.gl/nDdtPnUbuoFMJiVK6" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="google-reviews-link"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              <Star className="w-5 h-5" />
              Read Our Google Reviews
            </a>
            <a 
              href="https://maps.app.goo.gl/nDdtPnUbuoFMJiVK6" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="google-maps-link"
              className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              <MapPin className="w-5 h-5" />
              Find Us on Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Payment Methods Section */}
      <section data-testid="payment-section" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Payment Methods</h2>
            <p className="text-xl text-gray-600">We make it easy for you to pay</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center p-6 bg-blue-50 rounded-lg">
                <CreditCard className="w-10 h-10 mx-auto mb-4 text-blue-600" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Credit & Debit Cards</h3>
                <p className="text-gray-600">Visa, Mastercard, American Express, Discover</p>
              </div>
              
              <div className="text-center p-6 bg-green-50 rounded-lg">
                <Banknote className="w-10 h-10 mx-auto mb-4 text-green-600" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Cash</h3>
                <p className="text-gray-600">We accept cash payments</p>
              </div>
              
              <div className="text-center p-6 bg-purple-50 rounded-lg">
                <Smartphone className="w-10 h-10 mx-auto mb-4 text-purple-600" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Digital Payments</h3>
                <p className="text-gray-600">Venmo, Zelle, Cash App</p>
              </div>
              
              <div className="text-center p-6 bg-yellow-50 rounded-lg">
                <FileCheck className="w-10 h-10 mx-auto mb-4 text-yellow-600" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Checks</h3>
                <p className="text-gray-600">Personal and business checks accepted</p>
              </div>
            </div>
            
            <div className="mt-8 p-6 bg-blue-100 rounded-lg text-center">
              <p className="text-lg font-semibold text-blue-900">
                Payment is due upon completion of service
              </p>
              <p className="text-gray-700 mt-2">
                We'll discuss all payment options when you schedule your appointment
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" data-testid="faq-section" className="py-20 bg-white scroll-mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">Got questions? We've got answers!</p>
          </div>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg overflow-hidden shadow-md">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex justify-between items-center hover:bg-gray-100 transition-colors"
                  data-testid={`faq-question-${index}`}
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  {openFAQ === index ? (
                    <ChevronUp className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-6 py-4 bg-white border-t border-gray-200">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="mt-12 text-center bg-blue-50 rounded-xl p-8">
            <p className="text-xl text-gray-800 mb-4">Still have questions?</p>
            <a
              href="tel:7866964010"
              onClick={() => trackEvent('click_to_call')}
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call Us: 786-696-4010
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact" data-testid="contact-section" className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white scroll-mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Schedule Your Service</h2>
            <p className="text-xl text-blue-200">Fill out the form below or call us at 786-696-4010</p>
          </div>
          
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-8 md:p-12 text-gray-900">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold mb-2">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your full name"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold mb-2">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="contact-phone-input"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(555) 555-5555"
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-semibold mb-2">Email *</label>
              <input
                type="email"
                id="email"
                name="email"
                data-testid="contact-email-input"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your@email.com"
              />
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold mb-2">Appliance Type *</label>
              <div data-testid="appliance-picker" className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {applianceOptions.map((option) => {
                  const OptionIcon = option.IconComponent;
                  const isSelected = formData.appliance_type === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      data-testid={`appliance-option-${option.value}`}
                      aria-pressed={isSelected}
                      onClick={() => { setFormData(prev => ({ ...prev, appliance_type: option.value })); setSubmitMessage(''); }}
                      className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${
                        isSelected ? 'border-blue-600 bg-blue-50 shadow-md' : 'border-gray-200 bg-white hover:border-blue-300'
                      }`}
                    >
                      {OptionIcon ? (
                        <OptionIcon className="w-10 h-10" />
                      ) : (
                        <Wrench className="w-10 h-10 p-1.5 text-blue-600" />
                      )}
                      <span className={`text-sm font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-700'}`}>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            
            <div className="mb-6">
              <label htmlFor="issue_description" className="block text-sm font-semibold mb-2">Issue Description *</label>
              <textarea
                id="issue_description"
                name="issue_description"
                data-testid="contact-issue-textarea"
                value={formData.issue_description}
                onChange={handleInputChange}
                required
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Please describe the issue with your appliance"
              ></textarea>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="preferred_datetime" className="block text-sm font-semibold mb-2">Preferred Date/Time</label>
                <input
                  type="text"
                  id="preferred_datetime"
                  name="preferred_datetime"
                  data-testid="contact-datetime-input"
                  value={formData.preferred_datetime}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Tomorrow morning"
                />
              </div>
              
              <div>
                <label htmlFor="address" className="block text-sm font-semibold mb-2">Address (Optional)</label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  data-testid="contact-address-input"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Your address"
                />
              </div>
            </div>
            
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={isSubmitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 px-6 rounded-lg text-lg font-bold transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Submitting...' : 'Request Service'}
            </button>
            
            {submitMessage && (
              <div data-testid="submit-message" className={`mt-6 p-4 rounded-lg text-center font-semibold ${
                submitMessage.includes('Thank you') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="footer" className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="/logo.png" alt="Appliances Repair MCulai logo" className="w-12 h-12 object-contain" />
                <h3 className="text-2xl font-bold">Appliances Repair MCulai</h3>
              </div>
              <p className="text-gray-400">Your trusted appliance repair experts in South Florida</p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
              <div className="space-y-2 text-gray-400">
                <p className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  <a href="tel:7866964010" onClick={() => trackEvent('click_to_call')} className="hover:text-white transition-colors">786-696-4010</a>
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  <a href="mailto:mr.culaimihai@gmail.com" className="hover:text-white transition-colors">mr.culaimihai@gmail.com</a>
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Business Hours</h4>
              <div className="text-gray-400">
                <p className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5" />
                  Sun–Fri: 7:30 AM – 9:00 PM
                </p>
                <p>Sat: 8:00 AM – 5:00 PM</p>
                <p className="mt-2 text-green-400 font-semibold">Same-Day Service Available</p>
              </div>
            </div>
          </div>
          
          {/* Service Area Directory */}
          <div className="border-t border-gray-800 pt-8 mb-8" data-testid="service-area-directory">
            <h4 className="text-lg font-semibold mb-6 text-center">Service Area Directory</h4>
            <div className="grid md:grid-cols-3 gap-8">
              {serviceAreaDirectory.map((county) => (
                <div key={county.county} data-testid={`county-${county.county.toLowerCase().replace(/\s+/g, '-')}`}>
                  <p className="font-bold text-white mb-1">{county.county}</p>
                  <p className="text-xs text-gray-500 mb-3">{county.tagline}</p>
                  <div className="flex flex-col gap-1.5 text-sm text-gray-400">
                    {county.areas.map((a) => (
                      <Link key={a.name} to={a.path} className="hover:text-white transition-colors">{a.name}</Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p className="mb-2">Serving Miami to Boynton Beach • Licensed & Insured • 7 Years Experience</p>
            <p>&copy; {new Date().getFullYear()} Appliances Repair MCulai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/book-online" element={<Home scrollToContact />} />
        {cities.filter((c) => c.hub).map((c) => (
          <Route key={c.slug} path={c.path} element={<CityPage slug={c.slug} />} />
        ))}
        <Route path="/appliance-repair/:citySlug" element={<CityPage />} />
        <Route path="*" element={<Home />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;